import React from 'react';
import { Link } from 'react-router-dom';
import { highlightText } from '../Highlight';
import ItemThumb from '../ItemThumb';

export const MONO_FONT = "'JetBrains Mono', ui-monospace, Menlo, monospace";
export const ARABIC_FONT =
  "'Noto Naskh Arabic', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Segoe UI Historic', sans-serif";

export interface ItemClass {
  key: string;
  label: string;
}

/** Arabic block. Used to flag part numbers that carry Arabic inside the code. */
const ARABIC_RE = /[؀-ۿ]/;

export function hasArabicInCode(partNumber?: string | null): boolean {
  return !!partNumber && ARABIC_RE.test(partNumber);
}

/** Master-sheet class prefixes → human label. Longest prefixes first. */
const CLASS_PREFIXES: Array<{ prefix: string; cls: ItemClass }> = [
  { prefix: 'SRV-', cls: { key: 'SRV', label: 'Services' } },
  { prefix: 'CON-', cls: { key: 'CON', label: 'Consumables' } },
  { prefix: 'MAT-', cls: { key: 'MAT', label: 'Materials' } },
  { prefix: 'EQP-', cls: { key: 'EQP', label: 'Equipment & Tools' } },
  { prefix: 'TOL-', cls: { key: 'EQP', label: 'Equipment & Tools' } },
  { prefix: 'F-', cls: { key: 'F', label: 'Fabrics' } },
  { prefix: 'B-', cls: { key: 'B', label: 'Beads & Sequins' } },
  { prefix: 'A-', cls: { key: 'A', label: 'Trims & Accessories' } },
];

export function classOfCode(partNumber?: string | null): ItemClass | null {
  const code = (partNumber || '').trim().toUpperCase();
  if (!code) return null;
  const hit = CLASS_PREFIXES.find((c) => code.startsWith(c.prefix));
  return hit ? hit.cls : null;
}

export interface ItemCellProps {
  partNumber?: string | null;
  description?: string | null;
  descriptionAr?: string | null;
  /** Small uppercase label after the code. Defaults to the class of the code. */
  classLabel?: string | null;
  linkTo?: string;
  /** Compact rows: code + English on one line, Arabic hidden. */
  compact?: boolean;
  /** Badges rendered after the English line (e.g. "Arabic inside the code"). */
  flags?: React.ReactNode;
  /** Search text: matching parts of the code and descriptions are wrapped in <mark>. */
  highlight?: string | null;
  /** Item pictures (2026-09-22): show a 36px thumbnail before the code, inside this same cell. */
  showThumb?: boolean;
  /** Presigned picture URL for this item; null/undefined draws the dashed placeholder. */
  pictureUrl?: string | null;
  /** Catalog item id, so the thumbnail's lightbox can link to the item card. */
  thumbItemId?: number | null;
  /** Presigned URLs expire after 15 minutes: refetch this id once. */
  onThumbError?: () => void;
}

const codeStyle: React.CSSProperties = {
  display: 'inline-block',
  font: `600 12.5px/1.3 ${MONO_FONT}`,
  color: '#0f3f8f',
  background: '#eef3fb',
  padding: '2px 6px',
  borderRadius: 4,
  whiteSpace: 'nowrap',
};

const ItemCell: React.FC<ItemCellProps> = ({
  partNumber,
  description,
  descriptionAr,
  classLabel,
  linkTo,
  compact,
  flags,
  highlight,
  showThumb,
  pictureUrl,
  thumbItemId,
  onThumbError,
}) => {
  const cls = classLabel === undefined ? classOfCode(partNumber)?.label ?? null : classLabel;
  const hl = (t?: string | null) => (highlight ? highlightText(t, highlight) : t);

  const code = partNumber ? (
    <span style={codeStyle}>{hl(partNumber)}</span>
  ) : null;

  const codeLine = code && (
    <span
      style={{
        display: compact ? 'inline' : 'block',
        marginRight: compact ? 8 : 0,
        marginBottom: compact ? 0 : 3,
      }}
    >
      {linkTo ? (
        <Link to={linkTo} style={{ textDecoration: 'none' }}>
          {code}
        </Link>
      ) : (
        code
      )}
      {cls && (
        <span
          style={{
            marginLeft: 6,
            fontSize: 11,
            color: '#8a94a6',
            textTransform: 'uppercase',
            letterSpacing: '.04em',
          }}
        >
          {cls}
        </span>
      )}
    </span>
  );

  const body = (
    <div style={{ lineHeight: 1.35, minWidth: 0 }}>
      {codeLine}
      {description && (
        <span style={{ display: compact ? 'inline' : 'block', color: 'rgba(0,0,0,0.88)' }}>
          {hl(description)}
        </span>
      )}
      {flags ? <span style={{ marginLeft: 6 }}>{flags}</span> : null}
      {!compact && descriptionAr && (
        <span
          dir="rtl"
          style={{
            unicodeBidi: 'plaintext',
            textAlign: 'right',
            display: 'block',
            fontFamily: ARABIC_FONT,
            fontSize: 13.5,
            lineHeight: 1.5,
            marginTop: 1,
            color: 'rgba(0,0,0,0.62)',
          }}
        >
          {hl(descriptionAr)}
        </span>
      )}
    </div>
  );

  // The thumbnail lives inside the Item cell so no column is added; callers widen the column by ~46.
  if (!showThumb) return body;
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
      <ItemThumb
        url={pictureUrl}
        size="sm"
        itemId={thumbItemId}
        code={partNumber}
        description={description}
        descriptionAr={descriptionAr}
        onError={onThumbError}
      />
      {body}
    </div>
  );
};

export default ItemCell;
